/* Store locator map — sections/store-locator-map.liquid

   Initialises a Mapbox GL map inside <store-locator-map>. Configuration comes
   off the element's data-* attributes, which Liquid renders from the section
   settings and the theme's Mapbox token.

   Salon pins are fetched from a generated theme asset (see salonData() for
   why they cannot come from Liquid). Mapbox does the clustering natively via
   the source's `cluster` option — no plugin.

   The element also owns the design's search box and result list: geocoding a
   typed place through the Mapbox Geocoding API, sorting the salons by distance
   from that point, rendering the list and keeping list, popup and map centre
   in sync. Figma node 2034:6096. */

/* Marker palette, measured from Figma 2034:6270 (the cluster bubble):
     halo  66px circle, #B8957B at 75% opacity, Gaussian blur stdDeviation 4
     core  48px solid #E7D4BC
     count white, 14px medium

   Mapbox circle layers have no blur, so the halo is approximated with a second,
   larger circle at low opacity beneath the core — see addSalons(). */
const BRAND = {
  halo: "#b8957b",
  core: "#e7d4bc",
  count: "#ffffff",
};

/* Earth's mean radius in kilometres, for the great-circle distance used to sort
   and label results. The salon coordinates are good to a few hundred metres at
   worst (see context/09-stockists.md), so a spherical model is well within the
   error already present in the data — no need for an ellipsoidal one. */
const EARTH_RADIUS_KM = 6371;
const KM_PER_MILE = 1.609344;

/* Strings come from the section's locale keys via window.storeLocatorStrings,
   injected by sections/store-locator-map.liquid. The fallbacks keep the list
   readable if that global is ever missing rather than rendering "undefined". */
const STRINGS = () =>
  Object.assign(
    {
      get_directions: "Get directions",
      no_results: "No Salons found near that location.",
      search_failed: "Search is unavailable right now. Please try again.",
      location_denied: "We could not access your location. Search for a suburb instead.",
      results_count: "[count] Salons found",
    },
    window.storeLocatorStrings || {}
  );

/* Mapbox Standard basemap configuration — the greyscale treatment the design
   calls for. Kept in one place because it is applied twice: once on the Map
   constructor's `config` option so the first paint is already monochrome, and
   again from applyStandardTheme() on `load` as the fallback for GL versions
   that ignore the constructor option. */
const STANDARD_BASEMAP_CONFIG = (theme) => ({
  theme: theme || "monochrome",
  lightPreset: "day",
  show3dObjects: false,
  showPointOfInterestLabels: false,
  // Neutral greys, so the tan markers are the only colour on the page.
  colorWater: "hsl(220, 4%, 85%)",
  colorLand: "hsl(40, 20%, 98%)",
  colorPlaceLabels: "hsl(220, 8%, 18%)",
});

class StoreLocatorMap extends HTMLElement {
  connectedCallback() {
    // Safety net for the entry animation, armed here rather than in init():
    // init() only runs once Mapbox GL has loaded from the CDN, so a timer set
    // there would not start ticking until the very thing it guards against had
    // already resolved. Anchored to element upgrade, it covers a slow or failed
    // Mapbox load too — worst case the panel fades up over a still-grey map,
    // which beats leaving the search box invisible.
    setTimeout(() => this.classList.add("wt-locator--ready"), 3000);

    // The Mapbox script is `defer`red, so it may not have run yet when this
    // element upgrades. Wait for it rather than failing silently.
    this.whenMapboxReady()
      .then(() => this.init())
      .catch((err) => console.error("[store-locator-map]", err));
  }

  whenMapboxReady(timeout = 10000) {
    if (window.mapboxgl) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const tick = () => {
        if (window.mapboxgl) return resolve();
        if (Date.now() - started > timeout) {
          return reject(new Error("Mapbox GL JS did not load"));
        }
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  init() {
    const container = this.querySelector("[data-map]");
    if (!container) return;

    const token = this.dataset.token;
    if (!token) return;

    const lng = parseFloat(this.dataset.lng);
    const lat = parseFloat(this.dataset.lat);
    const zoom = parseFloat(this.dataset.zoom);

    window.mapboxgl.accessToken = token;

    const style = this.dataset.style || "mapbox://styles/mapbox/light-v11";
    // Only the Standard style has a config schema; passing `config` to any
    // other style is ignored at best and throws at worst.
    this.isStandard = style.includes("/standard");

    this.map = new window.mapboxgl.Map({
      container,
      style,
      // Guard against a blank settings field: a NaN centre puts the map at
      // null island with no error, which reads as "the map is broken".
      center: [Number.isFinite(lng) ? lng : 151.2093, Number.isFinite(lat) ? lat : -33.8688],
      zoom: Number.isFinite(zoom) ? zoom : 10,
      cooperativeGestures: true,
      // The design calls for a clean, chromeless map: no zoom buttons, no
      // Mapbox wordmark and no attribution strip. `attributionControl: false`
      // suppresses the "© Mapbox © OpenStreetMap" bar; the wordmark is hidden
      // in section-store-locator-map.css, since Mapbox GL offers no option for
      // it. NOTE: hiding these requires a Mapbox plan that permits it — the
      // standard web terms require the logo and attribution to stay visible.
      attributionControl: false,
      // Applied at construction rather than on `load`, so the very first paint
      // is already monochrome. setConfigProperty in the load handler repaints a
      // frame or two later, which shows as a flash of the Standard style's
      // default full-colour basemap. applyStandardTheme() still runs on load —
      // it is the fallback for GL versions that ignore this option, and it is
      // idempotent, so setting the values twice costs nothing.
      config: this.isStandard ? { basemap: STANDARD_BASEMAP_CONFIG(this.dataset.theme) } : undefined,
    });

    this.map.on("load", () => {
      this.applyStandardTheme();
      if (this.dataset.boost === "true") this.boostContrast();
      this.addSalons();
    });

    // The entry animation is driven off this class rather than running on page
    // load, so the map fades up with tiles already drawn instead of revealing a
    // grey box. `idle` rather than `load`: load fires when the style is parsed,
    // idle when the first frame has actually finished rendering.
    // Normal path: reveal as soon as the first frame has actually rendered, so
    // the map fades up with tiles drawn rather than as a grey box. The fallback
    // timer in connectedCallback() covers the case where this never fires.
    this.map.once("idle", () => this.classList.add("wt-locator--ready"));

    // A URL-restricted token fails at request time, not at init, so surface it
    // here — otherwise the map is silently grey.
    this.map.on("error", (e) => {
      console.error("[store-locator-map] Mapbox error:", e?.error?.message || e);
    });

    // The theme editor resizes sections without a window resize event, and the
    // section may also be hidden when first laid out; both leave the map sized
    // to 0. Re-measure whenever the container's box changes.
    if ("ResizeObserver" in window) {
      this.observer = new ResizeObserver(() => this.map.resize());
      this.observer.observe(container);
    }

    this.initSearch();
  }

  /* Search box and result list ------------------------------------------- */

  initSearch() {
    this.form = this.querySelector("[data-search-form]");
    this.input = this.querySelector("[data-search-input]");
    this.list = this.querySelector("[data-results-list]");
    this.status = this.querySelector("[data-status]");
    this.clearButton = this.querySelector("[data-clear]");
    this.geolocateButton = this.querySelector("[data-geolocate]");

    // The panel is optional markup — the section renders it, but an older
    // published template may not, and the map must still work without it.
    if (!this.form || !this.list) return;

    this.resultLimit = parseInt(this.dataset.results, 10) || 4;
    this.mobileResultLimit = Math.min(this.resultLimit, 3);
    this.mobileResultsQuery = window.matchMedia("(max-width: 899px)");
    this.mobileResultsQuery.addEventListener?.("change", () => this.renderResults());
    this.units = this.dataset.units === "mi" ? "mi" : "km";

    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.search(this.input.value.trim());
    });

    this.clearButton?.addEventListener("click", () => this.clearSearch());

    // Geolocation is a browser capability, not a setting: reveal the control
    // only where it exists rather than showing a button that cannot work.
    // It also requires a secure context, where the API is present but always
    // errors — checking isSecureContext keeps that case out of the UI too.
    if (navigator.geolocation && window.isSecureContext && this.geolocateButton) {
      this.geolocateButton.hidden = false;
      this.geolocateButton.addEventListener("click", () => this.geolocate());
    }

    // `input` rather than `keyup` so the native clear affordance on a
    // type=search field toggles the button too.
    this.input?.addEventListener("input", () => this.syncClearButton());

    // Clicking a result opens the same popup a pin click would, so the two
    // routes to a salon behave identically. Delegated: the list is re-rendered
    // on every search, and per-row listeners would be rebound each time.
    this.list.addEventListener("click", (e) => {
      const row = e.target.closest("[data-salon-index]");
      if (!row) return;
      // Let the phone and directions links do their own thing.
      if (e.target.closest("a")) return;
      this.selectResult(parseInt(row.dataset.salonIndex, 10));
    });
  }

  syncClearButton() {
    if (!this.clearButton) return;
    this.clearButton.hidden = !this.input?.value;
  }

  clearSearch() {
    if (this.input) this.input.value = "";
    this.syncClearButton();
    this.origin = null;
    this.popup?.remove();
    // Falls back to listing what is nearest the current view, so clearing the
    // box empties the field but never the panel.
    this.renderResults();
  }

  async search(query) {
    if (!query) return this.clearSearch();

    const params = new URLSearchParams({
      access_token: this.dataset.token,
      limit: "1",
      language: "en",
      // Salons are in towns and suburbs, so restrict the match to place-like
      // features: an unrestricted search happily returns a shop or a street
      // with the same name and drops the map somewhere unhelpful.
      types: "place,locality,postcode,region,district,neighborhood,address",
    });
    if (this.dataset.country) params.set("country", this.dataset.country);

    const url =
      "https://api.mapbox.com/geocoding/v5/mapbox.places/" +
      encodeURIComponent(query) +
      ".json?" +
      params;

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const centre = data?.features?.[0]?.center;

      if (!centre) {
        this.origin = null;
        return this.renderResults([], STRINGS().no_results);
      }

      this.origin = { lng: centre[0], lat: centre[1] };
      this.popup?.remove();
      this.map.easeTo({ center: centre, zoom: 11 });
      this.renderResults();
    } catch (err) {
      console.error("[store-locator-map] geocoding failed:", err);
      this.renderResults([], STRINGS().search_failed);
    }
  }

  geolocate() {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.origin = { lng: pos.coords.longitude, lat: pos.coords.latitude };
        if (this.input) this.input.value = "";
        this.syncClearButton();
        this.popup?.remove();
        this.map.easeTo({ center: [this.origin.lng, this.origin.lat], zoom: 11 });
        this.renderResults();
      },
      (err) => {
        console.warn("[store-locator-map] geolocation refused:", err?.message || err);
        this.renderResults([], STRINGS().location_denied);
      }
    );
  }

  /* Distance --------------------------------------------------------------

     Haversine great-circle distance. Equirectangular would be cheaper and is
     accurate enough over a city, but the list spans Australia and New Zealand
     and its error grows with both distance and latitude — the wrong trade for
     a set that is sorted, not just labelled. */
  distanceFrom(origin, coords) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(coords[1] - origin.lat);
    const dLng = toRad(coords[0] - origin.lng);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(origin.lat)) * Math.cos(toRad(coords[1])) * Math.sin(dLng / 2) ** 2;
    const km = 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)));
    return this.units === "mi" ? km / KM_PER_MILE : km;
  }

  formatDistance(value) {
    // One decimal, matching the design's "0.3km" — and honest about data that
    // is only good to a few hundred metres anyway.
    return `${value.toFixed(1)}${this.units}`;
  }

  currentResultLimit() {
    return this.mobileResultsQuery?.matches ? this.mobileResultLimit : this.resultLimit;
  }

  renderResults(features, message) {
    if (!this.list) return;

    // With no search run, sort by what is nearest the middle of the current
    // view so the panel always reflects what the visitor is looking at.
    const origin = this.origin || {
      lng: this.map.getCenter().lng,
      lat: this.map.getCenter().lat,
    };

    let rows = features;
    if (!rows) {
      rows = (this.features || [])
        .map((f) => ({ feature: f, distance: this.distanceFrom(origin, f.geometry.coordinates) }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, this.currentResultLimit());
    }

    if (!rows.length) {
      const text = message || STRINGS().no_results;
      this.list.innerHTML = `<li class="wt-locator__empty">${esc(text)}</li>`;
      if (this.status) this.status.textContent = text;
      return;
    }

    // Held so a click on a row can find its feature again without re-sorting.
    this.rows = rows;

    this.list.innerHTML = rows
      .map((row, i) => this.resultMarkup(row.feature.properties, row.distance, i))
      .join("");

    if (this.status) {
      this.status.textContent = STRINGS().results_count.replace("[count]", rows.length);
    }

  }

  resultMarkup(p, distance, index) {
    const line2 = [p.city, p.state, p.postcode].filter(Boolean).join(", ");
    const strings = STRINGS();

    return `
      <li class="wt-locator__result" data-salon-index="${index}">
        <div class="wt-locator__result-head">
          <span class="wt-locator__result-pin" aria-hidden="true"></span>
          <p class="wt-locator__result-name">${esc(p.name)}</p>
          <span class="wt-locator__result-distance">${esc(this.formatDistance(distance))}</span>
        </div>
        <div class="wt-locator__result-body">
          <div class="wt-locator__result-address">
            ${p.address ? `<p class="wt-locator__result-line">${esc(p.address)}</p>` : ""}
            ${line2 ? `<p class="wt-locator__result-line">${esc(line2)}</p>` : ""}
            ${
              p.phone
                ? `<a class="wt-locator__result-phone" href="tel:${esc(
                    p.phone.replace(/\s+/g, "")
                  )}">${esc(p.phone)}</a>`
                : ""
            }
          </div>
          <a
            class="wt-locator__result-directions"
            href="${esc(directionsUrl(p))}"
            target="_blank"
            rel="noopener"
            title="${esc(strings.get_directions)}"
          >
            <span class="visually-hidden">${esc(strings.get_directions)}</span>
            <span class="wt-locator__result-directions-icon" aria-hidden="true"></span>
          </a>
        </div>
      </li>`;
  }

  selectResult(index) {
    const row = this.rows?.[index];
    if (!row) return;

    const coords = row.feature.geometry.coordinates;
    this.map.easeTo({ center: coords, zoom: Math.max(this.map.getZoom(), 13) });
    this.showPopup(coords, row.feature.properties);

    // Matched on the row's own index rather than its position among the list's
    // children, so the highlight still lands if the list ever holds anything
    // that is not a result row.
    for (const el of this.list.querySelectorAll("[data-salon-index]")) {
      el.classList.toggle("is-active", parseInt(el.dataset.salonIndex, 10) === index);
    }
  }

  applyStandardTheme() {
    /* Mapbox Standard is configured through style properties rather than by
       restyling layers. `theme: 'monochrome'` is the greyscale treatment, and
       unlike light-v11 it keeps the full road hierarchy — including
       `road-number-shield` and `road-exit-shield`, which light-v11 does not
       contain at all (50 layers vs 150).

       setConfigProperty throws on styles that have no config schema, so this
       is both feature-detected and guarded. */
    if (!this.isStandard) return;
    if (typeof this.map.setConfigProperty !== "function") return;

    const set = (key, value) => {
      try {
        this.map.setConfigProperty("basemap", key, value);
      } catch (err) {
        console.warn(`[store-locator-map] config ${key} not applied:`, err?.message || err);
      }
    };

    // Same values the constructor already applied via `config`; re-applying is
    // idempotent and covers GL versions that ignore that option.
    for (const [key, value] of Object.entries(STANDARD_BASEMAP_CONFIG(this.dataset.theme))) {
      set(key, value);
    }
  }

  boostContrast() {
    /* Mapbox's Light style is a deliberately desaturated canvas: its label
       colours sit at 49-69% lightness and its road casings at 100%, so on a
       near-white ground the labels look washed out and the roads barely read.
       That is correct for a data-overlay backdrop and wrong for a store
       locator, where the visitor needs to recognise their own suburb.

       This darkens type and gives roads an edge at runtime. It is a stopgap:
       the durable fix is a custom style in Mapbox Studio, because these layer
       ids are Mapbox's own and can change when the style version does. Every
       call is guarded by getLayer() so a renamed layer is skipped rather than
       throwing. */
    // Standard configures itself through applyStandardTheme(); these layer
    // ids belong to light-v11 and do not exist there.
    if (this.dataset.style?.includes("/standard")) return;

    const setPaint = (layer, prop, value) => {
      if (this.map.getLayer(layer)) this.map.setPaintProperty(layer, prop, value);
    };

    // Type: near-black for places, mid-dark for everything else, so the
    // hierarchy survives rather than flattening to one weight.
    const PLACE = "hsl(220, 8%, 18%)";
    const MINOR = "hsl(220, 6%, 34%)";
    for (const id of ["settlement-major-label", "settlement-minor-label", "state-label", "country-label"]) {
      setPaint(id, "text-color", PLACE);
    }
    for (const id of ["settlement-subdivision-label", "road-label-simple", "airport-label", "poi-label"]) {
      setPaint(id, "text-color", MINOR);
    }

    // A white halo keeps type legible where it crosses roads or park fill.
    for (const id of [
      "settlement-major-label", "settlement-minor-label", "settlement-subdivision-label",
      "road-label-simple", "state-label", "poi-label",
    ]) {
      setPaint(id, "text-halo-color", "hsl(0, 0%, 100%)");
      setPaint(id, "text-halo-width", 1.4);
    }

    // Roads: pale grey rather than pure white, so the network is visible as
    // geometry without competing with the markers.
    setPaint("road-simple", "line-color", "hsl(220, 4%, 88%)");
    setPaint("road-pedestrian", "line-color", "hsl(220, 4%, 92%)");
    setPaint("road-path", "line-color", "hsl(220, 4%, 92%)");

    // Land and water pulled apart slightly so the coastline reads. Water is a
    // neutral grey rather than Mapbox's blue: the page is a greyscale base with
    // tan markers, and a blue ocean is the only chromatic note on it.
    setPaint("land", "background-color", "hsl(40, 20%, 98%)");
    setPaint("water", "fill-color", "hsl(220, 4%, 85%)");
    // The shoreline sits under the fill on some styles; matching it stops a
    // blue rim showing at the coast.
    setPaint("waterway", "line-color", "hsl(220, 4%, 82%)");
  }

  /* Stockist's REST payload -> the GeoJSON this map already speaks.

     Stockist returns a flat array of locations; every consumer below expects a
     FeatureCollection (`data.features`, `f.geometry.coordinates`,
     `f.properties.name`), so the shape is normalised here and nothing
     downstream changes.

     lat/lng arrive as STRINGS ("-37.71286179") and GeoJSON requires numbers,
     hence Number() and the isFinite guard — a NaN coordinate is not rejected by
     Mapbox, it silently drops the pin somewhere off the map.

     Coordinate order is [longitude, latitude]. GeoJSON is x,y — the reverse of
     how the API names them, and the classic way to land every pin in the wrong
     hemisphere.

     `handle` keeps the identifier under the same property name the generated
     asset used (Stockist's `loc_mg76q86v` was imported as `loc-mg76q86v`), so
     popup code keyed on it works with either source. Only the fields the map
     actually renders are copied across. */
  stockistToGeoJSON(rows) {
    const features = [];
    for (const r of rows || []) {
      const lng = Number(r.longitude);
      const lat = Number(r.latitude);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [lng, lat] },
        properties: {
          handle: String(r.id || "").replace(/_/g, "-"),
          name: r.name || "",
          address: r.address_line_1 || "",
          city: r.city || "",
          state: r.state || "",
          postcode: r.postal_code || "",
          country: r.country || "",
          phone: r.phone || "",
        },
      });
    }
    return { type: "FeatureCollection", features };
  }

  async salonData() {
    // Two possible sources, in order of preference.
    //
    // 1. Stockist (`data-stockist`) — the live list. The salons are maintained
    //    in the Stockist dashboard, and its locations endpoint is public and
    //    unauthenticated, so edits there appear here on the next page load with
    //    no re-export and no deploy.
    //
    // 2. `data-salons` — the generated theme asset, kept as a fallback. It is a
    //    static snapshot (scripts/export-salons-geojson.py) and goes stale the
    //    moment a salon changes, but it means a Stockist outage, a blocked
    //    request or a mistyped tag degrades to an out-of-date map rather than
    //    an empty one.
    //
    // Neither can be inlined by Liquid: `metaobjects.salon.values` is capped at
    // 50 entries on the storefront and there are 226 salons.
    const stockist = this.dataset.stockist;
    if (stockist) {
      try {
        const res = await fetch(stockist);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const rows = await res.json();
        const data = this.stockistToGeoJSON(rows);
        if (data.features.length) return data;
        // An empty array is a real answer, not a transport failure — an empty
        // or misconfigured Stockist account. Fall through to the asset so the
        // map still shows something.
        console.warn("[store-locator-map] Stockist returned no usable locations");
      } catch (err) {
        console.error("[store-locator-map] Stockist fetch failed, falling back to the bundled asset:", err);
      }
    }

    const url = this.dataset.salons;
    if (!url) return null;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      console.error("[store-locator-map] could not load salon data:", err);
      return null;
    }
  }

  async addSalons() {
    const data = await this.salonData();
    const count = data?.features?.length || 0;
    if (!count) {
      console.warn("[store-locator-map] no salon features to plot");
      return;
    }

    this.map.addSource("salons", {
      type: "geojson",
      data,
      cluster: true,
      clusterRadius: 50,
      clusterMaxZoom: 13,
    });

    // The design's soft halo is a blurred circle, which Mapbox cannot do on a
    // circle layer. Two stacked circles get close: a wide, low-opacity tan
    // disc with a feathered edge (circle-blur), then the solid sand core over
    // it. Radii step with the point count, per the design's 58 / 45 / 24
    // groupings, and the halo keeps the design's 66:48 ratio to the core.
    this.map.addLayer({
      id: "salon-cluster-halo",
      type: "circle",
      source: "salons",
      filter: ["has", "point_count"],
      paint: {
        "circle-color": BRAND.halo,
        "circle-opacity": 0.75,
        "circle-blur": 0.45,
        "circle-radius": ["step", ["get", "point_count"], 24, 10, 30, 50, 36],
      },
    });

    this.map.addLayer({
      id: "salon-clusters",
      type: "circle",
      source: "salons",
      filter: ["has", "point_count"],
      paint: {
        "circle-color": BRAND.core,
        "circle-radius": ["step", ["get", "point_count"], 17, 10, 22, 50, 26],
      },
    });

    this.map.addLayer({
      id: "salon-cluster-count",
      type: "symbol",
      source: "salons",
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 13,
      },
      paint: { "text-color": BRAND.count },
    });

    // Single salons get the same treatment at a smaller size, so a lone pin
    // reads as one member of the same family rather than a different marker.
    this.map.addLayer({
      id: "salon-point-halo",
      type: "circle",
      source: "salons",
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-color": BRAND.halo,
        "circle-opacity": 0.75,
        "circle-blur": 0.45,
        "circle-radius": 14,
      },
    });

    this.map.addLayer({
      id: "salon-points",
      type: "circle",
      source: "salons",
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-color": BRAND.core,
        "circle-radius": 9,
      },
    });

    // The halo extends past the core, so it is a hit target too — otherwise a
    // click on the soft edge of a marker does nothing.
    for (const layer of ["salon-clusters", "salon-cluster-halo"]) {
      this.map.on("click", layer, (e) => this.zoomToCluster(e));
    }
    for (const layer of ["salon-points", "salon-point-halo"]) {
      this.map.on("click", layer, (e) => this.openPopup(e));
    }

    for (const layer of [
      "salon-clusters",
      "salon-cluster-halo",
      "salon-points",
      "salon-point-halo",
    ]) {
      this.map.on("mouseenter", layer, () => {
        this.map.getCanvas().style.cursor = "pointer";
      });
      this.map.on("mouseleave", layer, () => {
        this.map.getCanvas().style.cursor = "";
      });
    }

    // Kept for the result list, which sorts and renders from the same set the
    // map is plotting rather than fetching its own copy.
    this.features = data.features;

    this.fitToSalons(data);
    this.renderResults();
  }

  zoomToCluster(e) {
    const feature = this.map.queryRenderedFeatures(e.point, {
      layers: ["salon-clusters", "salon-cluster-halo"],
    })[0];
    if (!feature) return;
    this.map
      .getSource("salons")
      .getClusterExpansionZoom(feature.properties.cluster_id, (err, zoom) => {
        if (err) return;
        this.map.easeTo({ center: feature.geometry.coordinates, zoom });
      });
  }

  openPopup(e) {
    const f = e.features?.[0];
    if (!f) return;
    const coords = f.geometry.coordinates.slice();

    // Keep the popup on the copy of the world the user clicked, rather than
    // flying to a duplicate when the map is wrapped past 180 degrees.
    while (Math.abs(e.lngLat.lng - coords[0]) > 180) {
      coords[0] += e.lngLat.lng > coords[0] ? 360 : -360;
    }

    this.showPopup(coords, f.properties);
  }

  /* Shared by pin clicks and result-row clicks, so both routes to a salon
     produce exactly the same popup. Only one is open at a time — the design
     shows a single card, and stacked popups obscure the pins behind them. */
  showPopup(coords, p) {
    const line2 = [p.city, p.state, p.postcode].filter(Boolean).join(", ");

    const html = `
      <div class="wt-locator__popup">
        <p class="wt-locator__popup-name">${esc(p.name)}</p>
        ${p.address ? `<p class="wt-locator__popup-line">${esc(p.address)}</p>` : ""}
        ${line2 ? `<p class="wt-locator__popup-line">${esc(line2)}</p>` : ""}
        ${
          p.phone
            ? `<a class="wt-locator__popup-phone" href="tel:${esc(
                p.phone.replace(/\s+/g, "")
              )}">${esc(p.phone)}</a>`
            : ""
        }
        <a class="wt-locator__popup-directions" href="${esc(
          directionsUrl(p)
        )}" target="_blank" rel="noopener">
          ${esc(STRINGS().get_directions)}
        </a>
      </div>`;

    this.popup?.remove();
    this.popup = new window.mapboxgl.Popup({ offset: 14, maxWidth: "260px" })
      .setLngLat(coords)
      .setHTML(html)
      .addTo(this.map);
  }

  fitToSalons(data) {
    // Only reframe when the merchant has not set an explicit starting view;
    // otherwise their centre/zoom settings would be silently overridden.
    if (this.dataset.fit !== "true") return;
    const b = new window.mapboxgl.LngLatBounds();
    for (const f of data.features) b.extend(f.geometry.coordinates);
    if (!b.isEmpty()) this.map.fitBounds(b, { padding: 60, maxZoom: 12, duration: 0 });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.map?.remove();
  }
}

if (!customElements.get("store-locator-map")) {
  customElements.define("store-locator-map", StoreLocatorMap);
}

/* Google Maps rather than Mapbox: this hands the visitor off to whatever
   navigation app their device already has, which is the point of the control.
   Addressed by name and address rather than lat/lng — a named destination
   survives the handoff to the native app, where a bare coordinate becomes a
   dropped pin with no label. */
function directionsUrl(p) {
  const line2 = [p.city, p.state, p.postcode].filter(Boolean).join(", ");
  const destination = [p.name, p.address, line2].filter(Boolean).join(", ");
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
}

function esc(value) {
  return String(value ?? "").replace(/[&<>"']/g, (c) => {
    return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
  });
}
